import { Add } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Button, Grid, Stack, TextField, Typography, InputLabel } from '@mui/material';
import { Box } from '@mui/system';
import AffairApiService from 'api/AffairApiService';
import UserApiService from 'api/UserApiService';
import InputDate from 'components/InputDate';
import AutocompleteCustom, { PropertyNameOptions, SelectTypeOptions } from 'components/Select';
import { addLoadingAction, removeLoadingAction } from 'store/actions/loadingsActions';
import SelectUser from 'components/SelectUser';
import useApi from 'hooks/useApi';
import useUserRoles from 'hooks/useUserRoles';
import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { closeDrawerAction } from 'store/actions/drawerActions';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import Affair from 'types/entities/Affair';
import Order from 'types/entities/Order';
import User from 'types/entities/User';
import TMOrderScope from 'types/entities/TMOrderScope';
import OrderApiService from 'api/OrderApiService';
import Scope from 'types/entities/Scope';
import ScopeApiService from 'api/ScopeApiService';
import { updateIds } from 'helpers/orderhelper';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { openDialogAction } from 'store/actions/dialogsActions';
import { Loader, MultiSelectAutoComplete } from 'components/index';
import MultiSelectUser from 'components/MultiSelectUser';
import { OrderType } from 'types/enums/OrderType';
import { OrdersTabState } from 'types/enums/OrdersTabState';
import { OrderStatus } from 'types/enums/OrderStatus';
import { getPurchaseOrderAction } from 'store/actions/purchaseOrderActions';
import PurchaseOrderTable from '../PurchaseOrder/PurchaseOrderTable';
import './OrderEditDrawer.scss';
import { useLocation } from 'react-router-dom';
import { formatDateToddMMYYY } from 'helpers/format';
import { ActionDescriptor } from 'types/enums/ActionDescriptor';

type ActionType = 'save_as_draft' | 'validate';

const OrderEditDrawer = () => {
  const { dispatch } = useAppState();
  const appState = useSelector((state) => state.app);
  const userRoles = useUserRoles();
  const drawerState = useSelector((state) => state.drawer);
  const purchaseOrder = useSelector((state) => state.purchaseOrder);
  const { management } = useSelector((state) => state);
  const { order, handleUpdateOrder, isPartial = false } = drawerState.data;
  const deliverable = useSelector((state) => state.deliverable);
  const [isInValidation, setIsInValidation] = useState(false);
  const [isMissingInputs, setIsMissingInputs] = useState(false);
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [selectedScopes, setSelectedScopes] = useState<Scope[]>([]);
  const [archivedScopes, setArchivedScopes] = useState<Scope[]>([]);
  const [consultants, setConsultants] = useState<User[]>([]);
  const [deliveryManagers, setDeliveryManagers] = useState<User[]>([]);
  const [representatives, setRepresentatives] = useState<User[]>([]);
  const [archivedDeliveryManagers, setArchivedDeliveryManagers] = useState<User[]>([]);
  const [archivedConsultants, setArchivedConsultants] = useState<User[]>([]);
  const [archivedRepresentatives, setArchivedRepresentatives] = useState<User[]>([]);
  const [selectedConsultants, setSelectedConsultants] = useState<User[]>([]);
  const [selectedDeliveryManager, setSelectedDeliveryManager] = useState<User[]>([]);
  const [selectedRepresentative, setSelectedRepresentative] = useState<User[]>([]);
  const [availableScopes, setAvailableScopes] = useState<Scope[]>([]);
  const [availableConsultants, setAvailableConsultants] = useState<User[]>([]);
  const [availableDeliveryManagers, setAvailableDeliveryManagers] = useState<User[]>([]);
  const [availableRepresentatives, setAvailableRepresentatives] = useState<User[]>([]);
  const [purchaseOrderOTP, setPurchaseOrderOTP] = useState();
  const [clients, setClients] = useState<User[]>([]);
  const [affairs, setAffairs] = useState<User[]>([]);
  const [t] = useTranslation();
  const isUpdateOrderLoading = useSelector((state) => state.loadings.updateOrderDrawerEdit) ?? 0;
  const [orderEdited, setOrderEdited] = useState<Order>(order);
  const { makeCall } = useApi();
  const location = useLocation();
  const userLocale = navigator.language || 'fr-FR';
  const dateFormat = userLocale !== 'en-US' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';

  useEffect(() => {
    if (orderEdited) {
      setOrderEdited({
        ...order,
        start_date: order.start_date || moment().toISOString(),
        end_date: order.end_date || moment().toISOString(),
        is_sign_mandatory: order.is_sign_mandatory ?? true,
        client_connection: order.client_connection,
        action_id: ActionDescriptor.UpdateCatalogOrder,
      });
      const fetchOrder = async () => {
        const editOrder = await makeCall(
          OrderApiService.fetchCurrentOrdersListManagement(appState?.customer?.id as number, {
            id: Number(orderEdited.id),
            size: 1,
          })
        );
        setSelectedScopes(editOrder?.orderForCustomer?.[0]?.tmscopes || []);
        setSelectedConsultants(editOrder?.orderForCustomer?.[0]?.tmconsultants || []);
        setSelectedDeliveryManager(editOrder?.orderForCustomer?.[0]?.tmaccountables || []);
        let representatives = [];
        if (order.tmdelegues && order.tmdelegues.length > 0) {
          representatives = order.tmdelegues;
        } else if (order.delegue) {
          representatives = [order.delegue];
        }
        setSelectedRepresentative(representatives);
      };
      fetchOrder();
      setAvailableScopes(order.scopes || []);
      setAvailableConsultants(order.consultants || []);
      setAvailableDeliveryManagers(order.delivery_managers || []);
      setAvailableRepresentatives(order.representatives || []);
    }
  }, [order, appState]);

  useEffect(() => {
    const missingInputs =
      !orderEdited?.affair_id ||
      !orderEdited?.name_spec ||
      !isDateValid(orderEdited?.start_date, orderEdited?.end_date) ||
      selectedScopes.length === 0 ||
      selectedDeliveryManager.length === 0 ||
      selectedConsultants.length === 0 ||
      !orderEdited?.client_id;
    setIsMissingInputs(missingInputs);
  }, [orderEdited, selectedScopes, selectedDeliveryManager, selectedConsultants]);

  useEffect(() => {
    const getPurchaseOrder = async () => {
      if (purchaseOrderOTP) {
        dispatch(addLoadingAction('purchaseorderlist'));
        const purchaseOrderList = await OrderApiService.fetchPurchaseOrder(purchaseOrderOTP);
        purchaseOrderList.forEach(
          (po: {
            name: string;
            poend: string;
            poref: string;
            reference: string;
            postart: string;
            is_valid: boolean;
          }) => {
            po.is_valid = false; // Set is_valid to false for all items
            po.reference = `${po.poref} (${formatDateToddMMYYY(po.postart)} - ${formatDateToddMMYYY(po.poend)})`;
            po.name = po.poref;
          }
        );
        dispatch(getPurchaseOrderAction(purchaseOrderList));
        dispatch(removeLoadingAction('purchaseorderlist'));
      }
    };
    getPurchaseOrder();
  }, [purchaseOrderOTP]);

  useEffect(() => {
    const getAffairs = async () => {
      if (appState.customer) {
        const res = await makeCall(
          AffairApiService.fetchByCustomerId(appState.customer?.id),
          'Error while loading affairs'
        );
        setAffairs(res);
      }
    };

    const getRepresentatives = async () => {
      const creatorsFetched = await UserApiService.fetchPossiblesDelegues();
      setRepresentatives(creatorsFetched);
    };

    const getClients = async () => {
      if (appState.customer) {
        const res = await makeCall(
          UserApiService.getClientsOnCustomer(appState.customer?.id, { is_archived: 0, size: -1 }),
          'Error while loading clients'
        );
        setClients(res?.datas || []);
      }
    };
    const getEditPurchaseOrder = async () => {
      const purchaseOrderList = await makeCall(
        OrderApiService.fetchEditPurchaseOrder(order?.id as number, order?.affair?.otp as string)
      );
      if (purchaseOrderList.length > 0) {
        purchaseOrderList.forEach(
          (po: {
            name: string;
            poend: string;
            poref: string;
            reference: string;
            postart: string;
            is_valid: boolean;
          }) => {
            po.reference = `${po.poref} (${formatDateToddMMYYY(po.postart)} - ${formatDateToddMMYYY(po.poend)})`;
            po.name = po.poref;
          }
        );
      }
      dispatch(getPurchaseOrderAction(purchaseOrderList));
    };
    getAffairs();
    getClients();
    getEditPurchaseOrder();
    getRepresentatives();
  }, [appState.customer]);

  const handleSubmit = useCallback(
    (actionType: ActionType) => async (event: React.MouseEvent<HTMLButtonElement>) => {
      setIsInValidation(true);
      const { ids: accountable_ids, archived: is_accountable_archived } = updateIds(
        selectedDeliveryManager,
        archivedDeliveryManagers
      );
      const { ids: consultant_ids, archived: is_consultant_archived } = updateIds(
        selectedConsultants,
        archivedConsultants
      );
      const { ids: delegue_ids, archived: is_delegue_archived } = updateIds(
        selectedRepresentative,
        archivedRepresentatives
      );

      const { ids: scope_ids, archived: is_scope_archived } = updateIds(selectedScopes, archivedScopes);

      const client_ids = orderEdited?.client?.id ? [orderEdited?.client?.id] : null;
      const is_client_archived = client_ids !== null ? [false] : [];

      const tmOrderScope: TMOrderScope = {
        id: 0, // This will be generated by the backend
        accountable_ids,
        consultant_ids,
        client_ids,
        scope_ids,
        delegue_ids,
        order_id: orderEdited.id ?? 0,
        is_scope_archived,
        is_accountable_archived,
        is_consultant_archived,
        is_client_archived,
        is_delegue_archived,
      };

      const updatedOrder = {
        ...orderEdited,
        tm_order_scope: tmOrderScope,
        tmscopes: selectedScopes,
        tmconsultants: selectedConsultants,
        tmaccountables: selectedDeliveryManager,
        tmclients: orderEdited.clients,
        tmdelegues: selectedRepresentative,
        order_type_id: OrderType.CATALOG,
        purchase_orders: purchaseOrder?.datas,
      };

      await handleUpdateOrder(updatedOrder);
      dispatch(closeDrawerAction());
    },
    [
      orderEdited,
      selectedDeliveryManager,
      archivedDeliveryManagers,
      selectedConsultants,
      archivedConsultants,
      selectedRepresentative,
      archivedRepresentatives,
      selectedScopes,
      archivedScopes,
      purchaseOrder,
      dispatch,
    ]
  );

  const handleChange = (e: React.FormEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    e.preventDefault();
    if (e.currentTarget.name === 'draft_reference' && e.currentTarget.value === '') {
      return setOrderEdited({ ...orderEdited, [e.currentTarget.name]: null });
    }
    return setOrderEdited({ ...orderEdited, [e.currentTarget.name]: e.currentTarget.value });
  };

  const handleSelectClient = (client: User | null | undefined) => {
    if (client) return setOrderEdited({ ...orderEdited, client, client_id: client.id });
    return setOrderEdited({ ...orderEdited, client: null, client_id: null });
  };

  const handleSelectOptions = (e: SelectTypeOptions | null, property: PropertyNameOptions) => {
    if (e && property === 'affair' && objectIsAffair(e)) {
      setPurchaseOrderOTP(e?.otp);
      return setOrderEdited({
        ...orderEdited,
        [`${property}`]: e,
        [`${property}_name`]: e.name,
        [`${property}_otp`]: e.otp,
        [`${property}_id`]: e.id,
      });
    }
    return setOrderEdited({ ...orderEdited, affair: null, affair_id: null });
  };

  const handleDateChange = (name: string, date: Moment | null) => {
    setOrderEdited({
      ...orderEdited,
      [name]: date ? moment(date).format('YYYY-MM-DD') : null, // Handle null for reset
    });
  };

  const handleClientConnectionSwitch = (value: boolean) => {
    setOrderEdited(() => ({ ...orderEdited, client_connection: value }));
  };

  const isDateValid = (startDate: string | undefined, endDate: string | undefined): boolean => {
    if (!startDate || !endDate) {
      return false;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return false;
    }
    return end >= start;
  };

  const handleClose = useCallback(() => {
    dispatch(closeDrawerAction());
  }, [dispatch]);

  const [possiblesCreators, setPossiblesCreators] = useState<User[]>([]);

  useEffect(() => {
    const getScopes = async () => {
      if (appState.customer?.id) {
        const scopesRetrieved = await ScopeApiService.fetchByCustomerId(appState.customer.id);
        setScopes(scopesRetrieved);
      }
    };
    getScopes();
  }, []);

  useEffect(() => {
    const getPossibleUsersCreators = async () => {
      if (userRoles.isOrderManager) {
        const creators = await UserApiService.fetchPossiblesDelegues();
        setPossiblesCreators(creators);
      }
    };
    getPossibleUsersCreators();
  }, [appState.roles]);

  useEffect(() => {
    const getConsultants = async () => {
      if (appState.customer && appState.customer.id) {
        const res = await makeCall(UserApiService.fetchPossiblesConsultants(), 'Unable to retrieve consultants');
        setConsultants(res);
      }
    };
    getConsultants();
  }, [appState.customer]);

  useEffect(() => {
    const getDeliveryManagers = async () => {
      if (appState.customer && appState.customer.id) {
        const res = await makeCall(
          UserApiService.fetchPossiblesDeliverableTeamLeaders(),
          'Unable to retrieve delivery managers'
        );
        setDeliveryManagers(res);
      }
    };
    getDeliveryManagers();
  }, [appState.customer]);

  const handleScopeAdded = useCallback((newScope: Scope) => {
    const scopeWithArchiveStatus = { ...newScope, is_archived: false };
    setScopes((prevScopes) => [...prevScopes, scopeWithArchiveStatus]);
  }, []);

  const handleScopeDeletionMessage = useCallback(() => {
    dispatch(
      openDialogAction({
        name: 'orderActionRestrictionDialog',
        data: {
          message: 'You are unable to perform this action. This scope is already associated with ongoing deliverables.',
        },
      })
    );
  }, [dispatch]);

  const handleDMUserDeletionMessage = useCallback(() => {
    dispatch(
      openDialogAction({
        name: 'orderActionRestrictionDialog',
        data: {
          message:
            'You are unable to perform this action. This delivery manager is already linked with ongoing deliverables.',
        },
      })
    );
  }, [dispatch]);

  const handleConsultantUserDeletionMessage = useCallback(() => {
    dispatch(
      openDialogAction({
        name: 'orderActionRestrictionDialog',
        data: {
          message:
            'You are unable to perform this action. This consultant is already linked with ongoing deliverables.',
        },
      })
    );
  }, [dispatch]);

  const handleScopeArchived = useCallback(
    (updatedScope: Scope) => {
      setScopes((prevScopes) =>
        prevScopes.map((scope) => (scope.id === updatedScope.id ? { ...scope, is_archived: true } : scope))
      );
      const deliverable_scope = deliverable?.order?.scopes;
      const existsById = deliverable_scope?.some((item) => item.id === updatedScope.id);
      if (!existsById) {
        setArchivedScopes((prevArchivedScopes) => [...prevArchivedScopes, updatedScope]);
      }
    },
    [orderEdited.scopes]
  );

  const handleScopesChange = useCallback((newSelectedScopes: Scope[]) => {
    setSelectedScopes(newSelectedScopes);
  }, []);

  const handleConsultantChange = useCallback((newSelectedConsultants: User[], reason?: string) => {
    setSelectedConsultants(newSelectedConsultants);
  }, []);

  const handleDeliveryManagerChange = useCallback((newSelectedDeliveryManager: User[], reason?: string) => {
    setSelectedDeliveryManager(newSelectedDeliveryManager);
  }, []);

  const handleRepresentativeChange = useCallback((newSelectedRepresentative: User[], reason?: string) => {
    setSelectedRepresentative(newSelectedRepresentative);
  }, []);

  const handleDeliveryManagerArchived = useCallback((updatedUser: User) => {
    setArchivedDeliveryManagers((prevArchivedDeliveryManager) => [...prevArchivedDeliveryManager, updatedUser]);
  }, []);

  const handleConsultantArchived = useCallback((updatedUser: User) => {
    setArchivedConsultants((prevArchiveConsultant) => [...prevArchiveConsultant, updatedUser]);
  }, []);

  const handleRepresentativeArchived = (updatedUser: User) => {
    setArchivedRepresentatives((prevArchiveRepresentative) => [...prevArchiveRepresentative, updatedUser]);
  };

  return (
    <>
      <Box sx={{ mb: 5 }}>
        <Grid container spacing={4}>
          {affairs && (
            <>
              <Grid item xs={12} lg={6}>
                <AutocompleteCustom
                  label={
                    <>
                      {t('Engagement_by_name_tm')}{' '}
                      <Typography display="inline" color="error">
                        *
                      </Typography>
                    </>
                  }
                  options={affairs}
                  optionsLabels="name"
                  handleSelectOptions={handleSelectOptions}
                  selectedOption={orderEdited?.affair}
                  propertyName="affair"
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <AutocompleteCustom
                  label={
                    <>
                      {t('Engagement_by_otp_code_tm')}{' '}
                      <Typography display="inline" color="error">
                        *
                      </Typography>
                    </>
                  }
                  options={affairs}
                  optionsLabels="otp"
                  handleSelectOptions={handleSelectOptions}
                  selectedOption={orderEdited?.affair}
                  propertyName="affair"
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} lg={6}>
            <InputLabel htmlFor="project_name">
              {t('Project_name_tm')}{' '}
              <Typography display="inline" color="error">
                *
              </Typography>
            </InputLabel>
            <TextField
              name="name_spec"
              id="name_spec"
              fullWidth
              placeholder={t('Name_spec')}
              onChange={handleChange}
              value={orderEdited?.name_spec}
              required
            />
          </Grid>

          <Grid item xs={12} lg={3}>
            {!isPartial && (
              <InputDate
                label={
                  <>
                    {t('Start_date_tm')}{' '}
                    <Typography display="inline" color="error">
                      *
                    </Typography>
                  </>
                }
                name="start_date"
                id="start_date"
                placeholder={dateFormat}
                onchange={handleDateChange}
                value={orderEdited?.start_date}
                required
              />
            )}
          </Grid>

          <Grid item xs={12} lg={3}>
            {!isPartial && (
              <InputDate
                label={
                  <>
                    {t('End_date_tm')}{' '}
                    <Typography display="inline" color="error">
                      *
                    </Typography>
                  </>
                }
                name="end_date"
                id="end_date"
                placeholder={dateFormat}
                onchange={handleDateChange}
                value={orderEdited?.end_date}
                required
                min={order?.start_date}
              />
            )}
          </Grid>

          <Grid
            item
            xs={12}
            lg={12}
            sx={{
              p: 1,
              position: 'relative',
              '& .MuiAvatarGroup-root': {
                display: 'flex',
                flexWrap: 'wrap', // Changed from wrap-reverse
                alignItems: 'center',
                justifyContent: 'flex-start', // Changed from flex-end
              },
            }}
          >
            <InputLabel htmlFor="scopes">
              {t('Scopes_tm')}
              <Typography display="inline" color="error">
                *
              </Typography>
            </InputLabel>{' '}
            <MultiSelectAutoComplete
              items={scopes} // Assuming 'scopes' is an array of { label: string, value: string }
              onChange={handleScopesChange}
              value={selectedScopes} // {selectedScopes} // Assuming you have a state 'selectedScopes' for this
              sx={{ width: '49%' }}
              selectAllLabel="Select scopes on the list or create your own"
              onScopeAdded={handleScopeAdded} // Pass down the callback
              onScopeArchived={handleScopeArchived} // Pass down the callback for archiving scopes
              availableScopes={availableScopes}
              onScopeDeletionAttempt={handleScopeDeletionMessage}
              maximumVisible={4}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 5 }}>
        <Typography sx={{ mb: 3, fontWeight: 'bold' }}>{t('Stake_holders_tm')}</Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} lg={6}>
            <InputLabel htmlFor="clientName">
              {t('Client_name_tm')}
              <Typography display="inline" color="error">
                *
              </Typography>
            </InputLabel>
            {clients && (
              <SelectUser
                users={clients}
                selectedUser={orderEdited?.client}
                onSelectUser={handleSelectClient}
                label=""
              />
            )}
          </Grid>

          {/* <Grid item xs={12} lg={6}>
            {userRoles.isOrderManager && (
              <SelectUser
                users={possiblesCreators}
                selectedUser={orderEdited?.delegue}
                onSelectUser={handleSelectDelegue}
                label={t('Representative_select_tm')}
              />
            )}
          </Grid> */}

          <Grid
            item
            xs={12}
            lg={6}
            sx={{
              p: 2,
              position: 'relative',
              '& .MuiAvatarGroup-root': {
                display: 'flex',
                flexWrap: 'wrap', // Changed from wrap-reverse
                alignItems: 'center',
                justifyContent: 'flex-start', // Changed from flex-end
              },
            }}
          >
            {userRoles.isOrderManager && (
              <>
                <InputLabel htmlFor="representative">{t('Representative_select_tm')} </InputLabel>
                <MultiSelectUser
                  users={representatives}
                  id="representative"
                  onSelectUsers={handleRepresentativeChange}
                  selectedUsers={selectedRepresentative}
                  onUserArchived={handleRepresentativeArchived}
                  label="Select Representative"
                  availableUsers={availableRepresentatives || []}
                  onUserDeletionAttempt={() => {
                    // intentionally left blank
                  }}
                  maximumVisible={3}
                />
              </>
            )}
          </Grid>

          <Grid
            item
            xs={12}
            lg={6}
            sx={{
              p: 2,
              position: 'relative',
              '& .MuiAvatarGroup-root': {
                display: 'flex',
                flexWrap: 'wrap', // Changed from wrap-reverse
                alignItems: 'center',
                justifyContent: 'flex-start', // Changed from flex-end
              },
            }}
          >
            <InputLabel htmlFor="deliverymangers">
              {t('Delivery_managers_tm')}{' '}
              <Typography display="inline" color="error">
                *
              </Typography>
            </InputLabel>
            <MultiSelectUser
              users={deliveryManagers}
              id="delivery-managers"
              onSelectUsers={handleDeliveryManagerChange}
              selectedUsers={selectedDeliveryManager}
              onUserArchived={handleDeliveryManagerArchived}
              label="Select Delivery Managers"
              availableUsers={availableDeliveryManagers || []}
              onUserDeletionAttempt={handleDMUserDeletionMessage}
              maximumVisible={3}
            />
          </Grid>

          <Grid
            item
            xs={12}
            lg={6}
            sx={{
              p: 2,
              position: 'relative',
              '& .MuiAvatarGroup-root': {
                display: 'flex',
                flexWrap: 'wrap', // Changed from wrap-reverse
                alignItems: 'center',
                justifyContent: 'flex-start', // Changed from flex-end
              },
            }}
          >
            <InputLabel htmlFor="consultants">
              {t('Consultants_tm')}
              <Typography display="inline" color="error">
                *
              </Typography>
            </InputLabel>
            <MultiSelectUser
              users={consultants}
              id="consultants"
              onSelectUsers={handleConsultantChange}
              selectedUsers={selectedConsultants}
              onUserArchived={handleConsultantArchived}
              label="Select Consultant"
              availableUsers={availableConsultants || []}
              onUserDeletionAttempt={handleConsultantUserDeletionMessage}
              maximumVisible={3}
            />
          </Grid>

          <Grid item xs={12} lg={6}>
            <Typography color="neutral" sx={{ mb: 1, fontWeight: 500 }}>
              {t('client_connection_platform')}
              <Typography display="inline" color="error">
                *
              </Typography>
            </Typography>
            <Stack direction="row" gap={2}>
              <Button
                sx={{ minWidth: 100 }}
                onClick={() => handleClientConnectionSwitch(true)}
                variant={orderEdited.client_connection ? 'contained' : 'outlined'}
                startIcon={orderEdited.client_connection ? <CheckCircleIcon /> : null}
              >
                {t('yes')}
              </Button>
              <Button
                sx={{ minWidth: 100 }}
                onClick={() => handleClientConnectionSwitch(false)}
                variant={!orderEdited.client_connection ? 'contained' : 'outlined'}
                startIcon={!orderEdited.client_connection ? <CheckCircleIcon /> : null}
              >
                {t('no')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <Typography sx={{ mt: 2, mb: 3, fontWeight: 'bold' }}>{t('purchase_order')}</Typography>

      <PurchaseOrderTable />

      <Grid container spacing={4} alignItems="center" justifyContent="space-between">
        <Grid item xs={12} md={4} lg={2}>
          <Button onClick={handleClose} disabled={false} color="success" variant="contained">
            {isInValidation ? <Loader size={20} className="white-loader" /> : t('Cancel_tm')}
          </Button>
        </Grid>

        <Grid item xs={12} md={2} lg={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Grid
            item
            sx={{
              display:
                management.pageHandler.currentTab === OrdersTabState.PENDING ||
                management.pageHandler.currentTab === OrdersTabState.TO_ASSIGN
                  ? 'none'
                  : '',
            }}
          >
            <Button
              color="success"
              variant="contained"
              sx={{ width: 150, mr: 2 }}
              onClick={handleSubmit('save_as_draft')}
              disabled={management.pageHandler.currentTab === OrdersTabState.ON_GOING}
            >
              {isInValidation ? <Loader size={20} className="white-loader" /> : t('Save_as_draft_tm')}
            </Button>
          </Grid>

          <Grid item>
            {userRoles.isOrderManager && (
              <LoadingButton
                variant="contained"
                onClick={handleSubmit('validate')}
                loading={isUpdateOrderLoading !== 0}
                startIcon={<Add />}
                color="success"
                disabled={isMissingInputs}
              >
                {t('Validate')}
              </LoadingButton>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
export default OrderEditDrawer;

const objectIsAffair = (obj: SelectTypeOptions): obj is Affair => {
  if (obj && Object.keys(obj).includes('otp')) {
    return true;
  }
  return false;
};
